import React, { useMemo, useState } from 'react';

import {
  Avatar,
  Box,
  Divider,
  Pagination,
  Rating,
  Stack,
  Typography,
} from '@mui/material';
import { htmlLineBreaksToNewlines } from '@realadvisor/agency-utils';
import { MessageSquareText } from 'lucide-react';

import { LoadingSpinner } from '@modules/_shared/components/LoadingSpinner';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { useEntityReviews } from '@modules/_shared/components/visibility-drawer/hooks/useEntityData';
import type { NormalizedReview } from '@modules/_shared/components/visibility-drawer/queries';
import DrawerEmptyState from '@modules/_shared/components/visibility-drawer/sections/DrawerEmptyState';
import EditableSectionHeader from '@modules/_shared/components/visibility-drawer/sections/EditableSectionHeader';
import { VerificationStatus } from '@modules/_shared/components/visibility-drawer/sections/VerificationStatus';
import type { SectionProps } from '@modules/_shared/components/visibility-drawer/types';
import {
  isAgencyEntity,
  isAgentEntity,
} from '@modules/_shared/components/visibility-drawer/types';
import { useIsTeamLeaderOrAdmin } from '@modules/_shared/hooks/useIsTeamLeaderOrAdmin';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { getInitialsFromDisplayName } from '@modules/_shared/utils/contact';
import { formatTimeAgo, roundRating } from '@modules/_shared/utils/formatting';
import { ReviewDrawer } from '@modules/reviews/ReviewDrawer';
import ReviewCtaButton from '@modules/visibility-reviews/components/ReviewCtaButton';
import { ReviewInvitationModal } from '@modules/visibility-reviews/components/ReviewInvitationModal';
import { useLocale } from '@shared/hooks/locale';

const ReviewItem = ({
  review,
  onOpen,
  isOwnReply,
}: {
  review: NormalizedReview;
  onOpen?: () => void;
  isOwnReply: boolean;
}) => {
  const { t, dateLocale } = useLocale();

  const formatReviewDate = (dateString: string) => {
    try {
      const { timeAgo } = formatTimeAgo(
        dateString,
        true,
        false,
        t,
        dateLocale,
        true,
        true,
      );
      return timeAgo;
    } catch (error) {
      console.warn('Failed to format review date:', error);
      return dateString;
    }
  };

  return (
    <Box
      component={onOpen == null ? 'div' : 'button'}
      type={onOpen == null ? undefined : 'button'}
      onClick={onOpen}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        width: '100%',
        py: 0.5,
        ...(onOpen == null
          ? {}
          : {
              px: 0,
              border: 0,
              borderRadius: 1,
              bgcolor: 'transparent',
              color: 'inherit',
              font: 'inherit',
              textAlign: 'left',
              cursor: 'pointer',
              '&:focus-visible': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: 2,
              },
            }),
      }}
    >
      <Avatar
        sx={{
          bgcolor: 'primary.main',
          width: 36,
          height: 36,
          fontSize: '0.875rem',
          fontWeight: 600,
          flexShrink: 0,
        }}
      >
        {getInitialsFromDisplayName(review.displayName)}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
            }}
          >
            {review.displayName ?? t('Anonymous')}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'text.disabled',
              flexShrink: 0,
            }}
          >
            {review.completedAt && formatReviewDate(review.completedAt)}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            mt: 0.25,
          }}
        >
          <Rating value={review.rating ?? 0} readOnly size="small" />
          {review.isVerified && (
            <VerificationStatus
              isVerified={true}
              label={t('Verified review')}
            />
          )}
        </Box>
        {review.reviewText && (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              mt: 0.75,
              lineHeight: 1.5,
            }}
          >
            {review.reviewText}
          </Typography>
        )}
        {review.ownerAnswer != null && review.ownerAnswer.trim() !== '' && (
          <Box
            sx={{
              mt: 1.25,
              pl: 1.5,
              borderLeft: '2px solid',
              borderColor: 'divider',
            }}
          >
            <Typography
              variant="caption"
              sx={{ display: 'block', fontWeight: 600, color: 'text.primary' }}
            >
              {isOwnReply ? t('Your reply') : t('Reply')}
              {review.ownerAnswerDate != null &&
                ` · ${formatReviewDate(review.ownerAnswerDate)}`}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                mt: 0.25,
                color: 'text.secondary',
                lineHeight: 1.5,
                whiteSpace: 'pre-line',
                overflowWrap: 'anywhere',
              }}
            >
              {htmlLineBreaksToNewlines(review.ownerAnswer)}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const ReviewEmptyState = ({
  onGetMoreReviews,
  canAskForReviews,
}: {
  onGetMoreReviews: () => void;
  canAskForReviews: boolean;
}) => {
  const { t } = useLocale();
  return (
    <Stack spacing={2} sx={{ width: '100%' }}>
      <DrawerEmptyState
        icon={MessageSquareText}
        message={t(
          'You have not yet received reviews, ask your satisfied clients to leave a review today.',
        )}
      />
      <ReviewCtaButton
        onClick={onGetMoreReviews}
        disabled={!canAskForReviews}
      />
    </Stack>
  );
};

const ReviewsHeader = ({
  totalCount,
  averageRating,
}: {
  totalCount: number;
  averageRating: number;
}) => {
  const { t } = useLocale();
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          mb: 2,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {roundRating(averageRating).toFixed(1)}
        </Typography>
        <Rating
          value={roundRating(averageRating)}
          readOnly
          precision={0.1}
          size="small"
        />
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          ({totalCount} {t('reviews')})
        </Typography>
      </Box>
      <Divider sx={{ mb: 2 }} />
    </>
  );
};

const ReviewsSection = ({ entity, teamId }: SectionProps) => {
  const { t } = useLocale();
  const { me } = useAppData();
  const [openInvitationModal, setOpenInvitationModal] = useState(false);
  const [selectedReviewId, setSelectedReviewId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const canManageTeam = useIsTeamLeaderOrAdmin(teamId);

  // Check if viewing own profile
  const isMyOwnProfile = useMemo(() => {
    return isAgentEntity(entity) && entity.id === me?.id;
  }, [entity, me?.id]);

  // Check if reviews can be sent based on entity type and permissions
  const canAskForReviews = useMemo(() => {
    if (isAgentEntity(entity)) {
      const isProfilePublished = !!entity.show_in_agency_pages;
      if (canManageTeam) {
        return isProfilePublished;
      }
      return isMyOwnProfile && isProfilePublished;
    }
    if (isAgencyEntity(entity)) {
      const hasPublishedMembers =
        entity.teams_users?.some(
          tu => tu.user.show_in_agency_pages && !tu.deactivated,
        ) ?? false;
      return hasPublishedMembers && canManageTeam;
    }
    return false;
  }, [entity, canManageTeam, isMyOwnProfile]);

  const { data, totalCount, averageRating, loading, error, setErrorLike } =
    useEntityReviews(entity, {
      teamId,
      limit: itemsPerPage,
      offset: (currentPage - 1) * itemsPerPage,
    });

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const hasReviews = data.length > 0;
  const isInitialLoading = loading && !totalCount;

  const reviewsContent = useMemo(() => {
    if (isInitialLoading) {
      return <LoadingSpinner />;
    }

    if (!hasReviews && !loading) {
      return (
        <ReviewEmptyState
          onGetMoreReviews={() => setOpenInvitationModal(true)}
          canAskForReviews={canAskForReviews}
        />
      );
    }

    return (
      <Stack
        direction="column"
        spacing={2}
        sx={{
          width: '100%',
        }}
      >
        <ReviewsHeader
          totalCount={totalCount}
          averageRating={averageRating ?? 0}
        />
        <Stack direction="column">
          {loading ? (
            <Stack
              sx={{
                alignItems: 'center',
                py: 4,
              }}
            >
              <LoadingSpinner />
            </Stack>
          ) : (
            data.map((review, index) => (
              <Box key={review.id}>
                <ReviewItem
                  review={review}
                  isOwnReply={isMyOwnProfile}
                  onOpen={
                    isAgentEntity(entity)
                      ? () => setSelectedReviewId(review.id)
                      : undefined
                  }
                />
                {index < data.length - 1 && <Divider sx={{ my: 3 }} />}
              </Box>
            ))
          )}
        </Stack>
        {totalPages > 1 && (
          <Stack
            sx={{
              alignItems: 'center',
              mt: 3,
            }}
          >
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
            />
          </Stack>
        )}
        <Box sx={{ mt: 3 }}>
          <ReviewCtaButton
            onClick={() => setOpenInvitationModal(true)}
            disabled={!canAskForReviews}
          />
        </Box>
      </Stack>
    );
  }, [
    isInitialLoading,
    hasReviews,
    loading,
    totalCount,
    averageRating,
    data,
    totalPages,
    currentPage,
    canAskForReviews,
    entity,
    isMyOwnProfile,
  ]);

  if (error && !data.length) {
    console.error('Error loading reviews:', error);
    return (
      <EditableSectionHeader title={t('Reviews')}>
        <Typography sx={{ color: 'error.main' }}>
          {t('Error loading reviews')}
        </Typography>
      </EditableSectionHeader>
    );
  }

  return (
    <>
      <EditableSectionHeader title={t('Reviews')}>
        {reviewsContent}
      </EditableSectionHeader>

      <ReviewInvitationModal
        open={openInvitationModal}
        onClose={() => setOpenInvitationModal(false)}
        initialAgentId={isAgentEntity(entity) ? entity.id : undefined}
      />

      {selectedReviewId != null && (
        <ReviewDrawer
          reviewId={selectedReviewId}
          onClose={() => setSelectedReviewId(null)}
        />
      )}

      <MutationErrorSnackbar error={error} onClose={() => setErrorLike(null)} />
    </>
  );
};

export default ReviewsSection;
